export type SectionId = 'repairList';

export interface FormField {
  id: string;
  label: string;
  section: SectionId;
  hasCheckbox: boolean;
  checkboxCount?: number;
}

export const SECTION_LABELS: Record<SectionId, string> = {
  repairList: 'Repair List',
};

export const SECTION_ORDER: SectionId[] = ['repairList'];

export const INSPECTION_FIELDS: FormField[] = [
  { id: 'xGuardRemoval', label: 'X-GUARD REMOVAL', section: 'repairList', hasCheckbox: true },
  { id: 'removeHeadacheRack', label: 'REMOVE HEADACHE RACK', section: 'repairList', hasCheckbox: true },
  { id: 'leftWallDamaged', label: 'LEFT WALL DAMAGED', section: 'repairList', hasCheckbox: true },
  { id: 'leftDoorDamaged', label: 'LEFT DOOR DAMAGED', section: 'repairList', hasCheckbox: true },
  { id: 'backWallDamaged', label: 'BACK WALL DAMAGED', section: 'repairList', hasCheckbox: true },
  { id: 'rightWallDamaged', label: 'RIGHT WALL DAMAGED', section: 'repairList', hasCheckbox: true },
  { id: 'rightDoorDamaged', label: 'RIGHT DOOR DAMAGED', section: 'repairList', hasCheckbox: true },
  { id: 'roofDamaged', label: 'ROOF DAMAGED', section: 'repairList', hasCheckbox: true },
  { id: 'extenders', label: 'EXTENDERS', section: 'repairList', hasCheckbox: true, checkboxCount: 6 },
  { id: 'skirts', label: 'SKIRTS (ASSEMBLY)', section: 'repairList', hasCheckbox: true, checkboxCount: 4 },
  { id: 'assembly1', label: '(ASSEMBLY)', section: 'repairList', hasCheckbox: true },
  { id: 'assembly2', label: '(ASSEMBLY)', section: 'repairList', hasCheckbox: true },
  { id: 'assembly3', label: '(ASSEMBLY)', section: 'repairList', hasCheckbox: true },
  { id: 'grille', label: 'GRILLE (ASSEMBLY)', section: 'repairList', hasCheckbox: true, checkboxCount: 2 },
  { id: 'headlights', label: 'HEADLIGHTS', section: 'repairList', hasCheckbox: true, checkboxCount: 2 },
  { id: 'airlines', label: 'AIRLINES', section: 'repairList', hasCheckbox: true },
  { id: 'clearanceLights', label: 'CLEARANCE LIGHTS', section: 'repairList', hasCheckbox: true },
  { id: 'fuelTank', label: 'FUEL TANK (ASSEMBLY)', section: 'repairList', hasCheckbox: true, checkboxCount: 1 },
  { id: 'cab', label: 'CAB', section: 'repairList', hasCheckbox: true },
];

export function getCheckboxCount(field: FormField): number {
  return field.hasCheckbox ? (field.checkboxCount ?? 1) : 0;
}

export function getFieldsBySection(section: SectionId): FormField[] {
  return INSPECTION_FIELDS.filter((field) => field.section === section);
}

/** Source scan dimensions used to calibrate PDF overlay positions. */
export const FORM_IMAGE = { width: 1275, height: 1650 };
export const PDF_SIZE = { width: 612, height: 792 };

export interface ImagePoint {
  x: number;
  y: number;
}

export interface FieldPdfLayout {
  checkboxes: ImagePoint[];
  comment: ImagePoint & { maxWidth: number };
}

export const HEADER_PDF_LAYOUT = {
  unit: { x: 400, y: 78 },
  driverCode: { x: 640, y: 78 },
  driverName: { x: 900, y: 78 },
  date: { x: 1120, y: 78 },
};

const commentCol = (y: number): FieldPdfLayout['comment'] => ({
  x: 520,
  y,
  maxWidth: 480,
});

/** Checkbox and comment positions in source-image coordinates (top-left origin). */
export const FIELD_PDF_LAYOUT: Record<string, FieldPdfLayout> = {
  leftWallDamaged: {
    checkboxes: [{ x: 1070, y: 295 }],
    comment: commentCol(295),
  },
  leftDoorDamaged: {
    checkboxes: [{ x: 1070, y: 340 }],
    comment: commentCol(340),
  },
  backWallDamaged: {
    checkboxes: [{ x: 1070, y: 385 }],
    comment: commentCol(385),
  },
  rightWallDamaged: {
    checkboxes: [{ x: 1070, y: 430 }],
    comment: commentCol(430),
  },
  rightDoorDamaged: {
    checkboxes: [{ x: 1070, y: 475 }],
    comment: commentCol(475),
  },
  roofDamaged: {
    checkboxes: [{ x: 1070, y: 520 }],
    comment: commentCol(520),
  },
  extenders: {
    checkboxes: [
      { x: 650, y: 580 },
      { x: 750, y: 580 },
      { x: 850, y: 580 },
      { x: 950, y: 580 },
      { x: 1050, y: 580 },
      { x: 720, y: 630 },
    ],
    comment: commentCol(605),
  },
  skirts: {
    checkboxes: [
      { x: 680, y: 690 },
      { x: 820, y: 690 },
      { x: 960, y: 690 },
      { x: 720, y: 740 },
    ],
    comment: commentCol(715),
  },
  assembly1: {
    checkboxes: [{ x: 1070, y: 745 }],
    comment: commentCol(745),
  },
  assembly2: {
    checkboxes: [{ x: 1070, y: 795 }],
    comment: commentCol(795),
  },
  assembly3: {
    checkboxes: [{ x: 1070, y: 845 }],
    comment: commentCol(845),
  },
  grille: {
    checkboxes: [
      { x: 1070, y: 900 },
      { x: 720, y: 950 },
    ],
    comment: commentCol(925),
  },
  headlights: {
    checkboxes: [
      { x: 950, y: 1000 },
      { x: 1070, y: 1000 },
    ],
    comment: commentCol(1000),
  },
  airlines: {
    checkboxes: [{ x: 1070, y: 1055 }],
    comment: commentCol(1055),
  },
  clearanceLights: {
    checkboxes: [{ x: 1070, y: 1110 }],
    comment: commentCol(1110),
  },
  fuelTank: {
    checkboxes: [{ x: 1070, y: 1165 }],
    comment: commentCol(1165),
  },
  cab: {
    checkboxes: [{ x: 1070, y: 1220 }],
    comment: commentCol(1220),
  },
  xGuardRemoval: {
    checkboxes: [{ x: 1070, y: 1275 }],
    comment: commentCol(1275),
  },
  removeHeadacheRack: {
    checkboxes: [{ x: 1070, y: 1330 }],
    comment: commentCol(1330),
  },
};

export function imageToPdf(point: ImagePoint): { x: number; y: number } {
  return {
    x: (point.x / FORM_IMAGE.width) * PDF_SIZE.width,
    y: PDF_SIZE.height - (point.y / FORM_IMAGE.height) * PDF_SIZE.height,
  };
}
